import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, Signal, ViewChild, ViewContainerRef } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { DetailsService } from '@features/details/data-access/details.service';
import { SkinningService } from '@core/skin';
import { UntilDestroy } from '@ngneat/until-destroy';
import { ItemMiniDetailsService } from '../data-access/mini-details.service';
import { MyContentStore } from '@features/my-content';
import { RecordingsStore } from '@features/live';
import { AnalyticEvent } from '@core/analytics';
import { ContentRatingGate } from '@core/content-ratings';
import { SettingsStore } from '@core/settings';
import { CommonModule } from '@angular/common';
import { ContentRatingLock } from '@shared/components/content-rating-lock/content-rating-lock';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { AnalyticsDirective } from 'app/shared/directives/google-analytics.directive';
import { PlayerService } from 'app/features/player/data-access/player.service';
import { clpp } from '@castlabs/prestoplay';
import { PlayerItemContentType, PlayerTypes } from 'app/features/player/data-access/player.config';
import { PlayerItem } from 'app/features/player/data-access/player.model';
import { BadgeComponent } from 'app/shared/components/badge/badge.component';
import { BadgePosition, BadgeVariant } from 'app/shared/components/badge/badge.model';
import { AssetSearchService } from '@features/assets/data-access';
import { formatAssetDuration } from '@shared/utils/duration.helper';

@UntilDestroy()
@Component({
  selector: 'mini-details',
  templateUrl: './mini-details.component.html',
  styleUrls: ['./mini-details.component.scss'],
  imports: [
    CommonModule,
    TranscodeImageDirective,
    RoundedIconButton,
    AnalyticsDirective,
    BadgeComponent,
    ContentRatingLock,
    TranslocoPipe,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MiniDetails implements OnDestroy {
  private itemMiniDetailsService = inject(ItemMiniDetailsService);
  private myContentStore = inject(MyContentStore);
  private recordingsStore = inject(RecordingsStore);
  private detailsService = inject(DetailsService);
  private elementRef = inject(ElementRef);
  private playerService = inject(PlayerService);
  private searchService = inject(AssetSearchService);
  public skinning = inject(SkinningService);
  public settings = inject(SettingsStore);
  private gate = inject(ContentRatingGate);

  public AnalyticEvent = AnalyticEvent;
  public enableAutoplayExtras = this.settings.enableAutoplayExtras;
  public item = this.itemMiniDetailsService.item;
  public playerMuted = this.playerService.isPlayerMuted();
  public expandMiniItem = this.itemMiniDetailsService.expandMiniItem;
  private itemConfig = this.itemMiniDetailsService.itemConfig;
  public deleteShow = computed(() => {
    return this.itemConfig()?.itemRemovable
  });
  // V2 DTO carries the trailer URL in `trailerFileName`; the player still reads `previewVideoUrl`.
  private previewVideoUrl = computed<string | undefined>(() => {
    const item = this.item();
    if (this.restricted()) return undefined;
    return item?.previewVideoUrl ?? item?.trailerFileName ?? undefined;
  });
  // The item already carries ng_source/ng_contentType (tagged at the API boundary / live
  // mappers); the player recomputes the EPG live-now source at play time.
  private playerItem = computed(() => ({ ...this.item(), previewVideoUrl: this.previewVideoUrl() }) as PlayerItem);
  /** Above the active profile's content rating: poster blurred, no play, no trailer preview (AVIION-36952). */
  public restricted = computed(() => this.gate.isRestricted(this.item()));
  public thumbnail = computed(() => {
    const item = this.item() as any;
    // EPG events carry their image in `imagePath` (backend name); VOD in poster/thumbnail*.
    const url: string | null | undefined = item?.thumbnailLandscape ?? item?.posterLandscape ?? item?.imagePath;
    // Transcoding happens in the [transcodeImage] directive on the poster.
    if (!url && item) {
      return this.skinning.getImage('icon_no_image');
    }
    return url;
  });

  // EPG display fields derived from the raw event's timeStart/timeEnd (backend names) — no upstream
  // mapper needed. Null for non-EPG items (VOD has no timeStart/timeEnd), so the template hides them.
  public airDate = computed(() => {
    const ms = toMs(this.item()?.timeStart);
    if (ms == null) return null;
    const d = new Date(ms);
    return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
  });
  public runningTime = computed(() => {
    const s = toMs(this.item()?.timeStart);
    const e = toMs(this.item()?.timeEnd);
    if (s == null || e == null) return null;
    const p = (n: number) => String(n).padStart(2, '0');
    const sd = new Date(s), ed = new Date(e);
    return `${p(sd.getHours())}:${p(sd.getMinutes())} - ${p(ed.getHours())}:${p(ed.getMinutes())}`;
  });
  // Same formatter as the full details overlay (prefers `durationInSeconds`, falls back to the
  // 25 fps frame count) so both read e.g. "1h 46min" instead of a raw number.
  public durationLabel = computed(() => formatAssetDuration(this.item()));
  public timelinePercentage = computed(() => {
    const s = toMs(this.item()?.timeStart);
    const e = toMs(this.item()?.timeEnd);
    if (s == null || e == null || e <= s) return 0;
    const now = Date.now();
    if (now <= s) return 0;
    if (now >= e) return 100;
    return 100 * (now - s) / (e - s);
  });
  public historicEpg = computed(() => {
    const e = toMs(this.item()?.timeEnd);
    return e != null ? e < Date.now() : false;
  });
  public previewVideoPlaying = computed(() => {
    const state = this.playerService.getState()();
    if (!this.previewVideoUrl()) {
      return false;
    }
    switch (state) {
      case clpp.Player.State.PLAYING: return !this.playerService.isPlayerPip()();
      case clpp.Player.State.ENDED:
      default: return false;
    }
  });
  public BadgeVariant = BadgeVariant;
  public showBadge = computed(() => {
    const item = this.item();
    return item != null && (item.badge != null || item.availabilityStartTime != null || item.availabilityEndTime != null);
  });
  public showAutomaticBadge = computed(() => this.item()?.contentType === PlayerItemContentType.EPISODE);
  // Offset from the poster edge, scaled so it stays proportional to the offset the badge had on
  // the originating carousel item (12px from the tile edge for top-right, 18px for bottom-center).
  public badgeOffset = computed(() => {
    const position = this.settings.badgePosition() as BadgePosition ?? BadgePosition.TopRight;
    const baseOffset = position === BadgePosition.BottomCenter ? 18 : 12;
    const itemWidth = this.itemMiniDetailsService.itemContainerElement()?.getBoundingClientRect().width;
    if (!itemWidth) return baseOffset;
    return baseOffset * (MINI_DETAILS_POSTER_WIDTH / itemWidth);
  });
  public isPlayDisabled: Signal<boolean> = computed(() => {
    const item = this.item();
    if (!item) return true;
    if (this.restricted()) return true;

    // Check availabilityStartTime (based on showPlayButton logic from hsx-episode-item):
    if (item.availabilityStartTime) {
      const startTime = new Date(item.availabilityStartTime).getTime();
      const endTime = new Date(item.availabilityEndTime).getTime();
      const now = Date.now();
      return now < startTime || now > endTime;
    }

    // EPG timing logic. timeStart/timeEnd are ISO strings on EPG items (new API format).
    const now = Date.now();
    const startMs = toMs(item.timeStart);
    const endMs = toMs(item.timeEnd);

    const isItemStartingInTheFuture = !!startMs && startMs > now;
    const isItemEndedInThePast = !!endMs && endMs < now && !this.historicEpg();

    return isItemStartingInTheFuture || isItemEndedInThePast;
  });

  private onLeaveHandler: (event: FocusEvent) => void;
  @ViewChild('playerContainer', { read: ViewContainerRef, static: false }) container!: ViewContainerRef;

  constructor() {
    effect(() => {
      if (this.itemMiniDetailsService.itemContainerElement()) {
        this.setMiniDetailsPosition();
      }
    })
    effect(() => {
      if (this.item()) {
        this.handlePreviewVideo();
        this.setMiniDetailsEvent();
      }
    })
  }

  ngOnDestroy() {
    const miniDetailsElem: HTMLElement = this.elementRef.nativeElement;
    const itemContainerElem: HTMLElement = this.itemMiniDetailsService.itemContainerElement();

    if (this.onLeaveHandler) {
      miniDetailsElem.removeEventListener('mouseleave', this.onLeaveHandler);
      this.itemMiniDetailsService.eventsReady.set(false);
      if (itemContainerElem) {
        itemContainerElem.removeEventListener('mouseleave', this.onLeaveHandler);
        this.itemMiniDetailsService.eventsReady.set(false);
      }
    }
  }

  private handlePreviewVideo() {
    if (this.playerService.isPlayerPip()() || !this.playerService.isPreviewAutoplayEnabled()) {
      return;
    }
    // Real playback engaged: don't start a hover trailer over it.
    if (this.playerService.isPreviewSuppressed()) return;
    if (this.previewVideoUrl()?.length) {
      setTimeout(() => {
        this.playerService.changePlayerViewLocation(this.container);
        this.playerService.playItem(this.playerItem(), PlayerTypes.MiniDetails);
      });
    }
  }

  private setMiniDetailsPosition(leftItemBorderOffset = 0): void {
    const miniDetailsElem: HTMLElement = this.elementRef.nativeElement;
    const itemContainerElem: HTMLElement = this.itemMiniDetailsService.itemContainerElement();
    const itemConfig = this.itemMiniDetailsService.itemConfig();
    const isParentCarousel = itemConfig?.isParentCarousel ?? false;
    const parentElementReference = itemConfig?.parentElementReference;

    if (!miniDetailsElem || !itemContainerElem) return;

    const rect = itemContainerElem.getBoundingClientRect();
    // An element that has left the DOM still measures as a 0x0 box at the origin, and the clamps
    // below then read that as "put it in the top-left corner". There is no sane position to give
    // a popup whose card is gone — drop it instead of parking it over the page.
    if (!itemContainerElem.isConnected || rect.width === 0 || rect.height === 0) {
      this.itemMiniDetailsService.removeItem();
      return;
    }
    const baseTop = rect.top + window.scrollY + rect.height / 2 - miniDetailsElem.offsetHeight / 2;
    const baseLeft = rect.left + window.scrollX + rect.width / 2 - miniDetailsElem.offsetWidth / 2;

    // Margins for carousel mode
    const leftMargin = isParentCarousel ? 50 : 0;
    const rightMargin = isParentCarousel ? 60 : 0;

    const clamp = (value: number, min: number, max: number) =>
      Math.max(min, Math.min(value, max));

    let top: number;
    let left: number;

    if (parentElementReference) {
      const parentRect = parentElementReference.getBoundingClientRect();
      const parentTop = parentRect.top + window.scrollY;
      const parentLeft = parentRect.left + window.scrollX;

      top = clamp(
        baseTop,
        parentTop,
        parentTop + parentRect.height - miniDetailsElem.offsetHeight
      );

      left = clamp(
        baseLeft,
        parentLeft + leftMargin,
        parentLeft + parentRect.width - miniDetailsElem.offsetWidth - rightMargin
      );
    } else {
      top = clamp(
        baseTop,
        window.scrollY,
        window.scrollY + window.innerHeight - miniDetailsElem.offsetHeight
      );

      left = clamp(
        baseLeft,
        window.scrollX + leftMargin,
        window.scrollX + window.innerWidth - miniDetailsElem.offsetWidth - rightMargin
      );
    }

    miniDetailsElem.style.position = 'absolute';
    miniDetailsElem.style.top = `${top}px`;
    miniDetailsElem.style.left = `${left + leftItemBorderOffset}px`;
  }


  private setMiniDetailsEvent() {
    const miniDetailsElem: HTMLElement = this.elementRef.nativeElement;
    const itemContainerElem: HTMLElement = this.itemMiniDetailsService.itemContainerElement();

    if (!itemContainerElem) {
      return;
    }

    this.onLeaveHandler = (event: FocusEvent) => {
      if (
        !miniDetailsElem.contains(event.relatedTarget as Node) &&
        !itemContainerElem.contains(event.relatedTarget as Node)
      ) {
        miniDetailsElem.removeEventListener('mouseleave', this.onLeaveHandler);
        itemContainerElem.removeEventListener('mouseleave', this.onLeaveHandler);
        this.itemMiniDetailsService.eventsReady.set(false);
        this.itemMiniDetailsService.removeItem();
      }
    };

    miniDetailsElem.addEventListener('mouseleave', this.onLeaveHandler);
    itemContainerElem.addEventListener('mouseleave', this.onLeaveHandler);
    this.itemMiniDetailsService.eventsReady.set(true);
  }

  public preventDefault(event: any): void {
    event.preventDefault();
    event.stopPropagation();
  }

  public openDetails(): void {
    this.reportSearchClick();
    this.detailsService.open({ item: this.item() });
    this.itemMiniDetailsService.removeItem();
  }

  public onPlayClick(event: MouseEvent) {
    if (this.isPlayDisabled()) return;
    this.reportSearchClick();
    // Kill the hover trailer preview first so the stream takes over at once and never plays behind it.
    this.playerService.stopPreview();
    this.playerService.changePlayerViewLocation();
    this.playerService.release().subscribe(() => {
      this.playerService.playItem(this.playerItem(), PlayerTypes.Normal);
      this.itemMiniDetailsService.removeItem();
    });
  }

  /**
   * Acting on a card that expanded over a search result counts as clicking that result, but the
   * click lands here instead of on the grid, so the grid can never report it (AVIION-36333).
   */
  private reportSearchClick(): void {
    if (this.itemConfig()?.isSearch) {
      this.searchService.trackClick(this.item());
    }
  }

  deleteItem(event: MouseEvent) {
    this.preventDefault(event);
    const referenceId = this.item()?.referenceId;
    if (!referenceId) return;

    const itemType = this.itemConfig()?.itemType;
    if (itemType === 'recording') {
      this.recordingsStore.removeRecording(referenceId).subscribe();
    } else if (itemType === 'favourite') {
      this.myContentStore.removeFromWatchLater([referenceId]).subscribe();
    }
    this.itemMiniDetailsService.removeItem();
  }

  public toggleMute(event: MouseEvent) {
    this.preventDefault(event);
    this.playerService.toggleMute();
  }

}

/** Width of the mini-details poster area in px — must match .mini-details-container in the SCSS. */
const MINI_DETAILS_POSTER_WIDTH = 432;

/** Parse an ISO string / epoch into ms, or null when absent/invalid. */
function toMs(value: string | number | null | undefined): number | null {
  if (value == null) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}
